import { useMemo, useState, RefObject, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import ExportIcon from '~/shared/assets/icons/export.svg';
import { ReportSelectionOption, SelectOption, Account } from '~/shared/utils/types';
import ChartFilterDropdown from '~/shared/components/ChartFilterDropdown';
import Button from '~/shared/components/ButtonsV2';
import VolumeViewDropdown from '~/shared/components/filters/VolumeViewDropdown';
import { LineChartHandle } from '~/shared/components/LineChart/LineChart';
import { useExportState } from '~/shared/context/ExportStateContext';
import { safeJsonParse, safeJsonParseArray } from '~/shared/utils/safeJson';
import {
  AUTOSCALING_ELIGIBILITY_PARAM_KEY,
  AUTOSCALING_STATE_PARAM_KEY,
  countAutoscalingFilterSelections,
  migrateLegacyAutoscalingUrlParams,
  stripAutoscalingFiltersFromApiFilters,
} from '~/shared/utils/autoscalingFilterUtils';
import { getVolumeViewPreset, VolumeViewPresetId } from '~/shared/utils/volumeViewPresets';

import { CUSTOM_DATES_TITLE, dateFilterItems } from '../common/dateFilterMenuItems';
import { apiFilterKey } from '../common/serachParamKeys';
import ReportsExportModal from '../ReportsPage/components/ReportsExportModal';

import ReportsFiltersSidePanel from './FiltersSidePanel/ReportsFiltersSidePanel';
import { PROCESSED_FILTERS_PARAM_KEY } from './FiltersSidePanel/utils/filterUtils';
import { useFilterUrlParams } from './FiltersSidePanel/hooks/useFilterUrlParams';

interface ReportsFilterSectionProps {
  activeDateFilterTitle: string | null;
  currentBaseFilters: ReportSelectionOption[];
  onDateFilterChange: (filter: {
    title: string;
    duration?: { start: Duration; end?: Duration };
  }) => void;
  onBaseFiltersChange: (newFilters: ReportSelectionOption[]) => void;
  setPageOffset: (offset: string) => void;
  utilizationChartRef: RefObject<LineChartHandle | null>;
  usageChartRef: RefObject<LineChartHandle | null>;
  tableQueryUrl: string;
  tableCountUrl: string;
  chartQueryUrl: string;
  totalCount: number | undefined;
  isOrgView?: boolean;
  accounts?: Account[];
  activeOrgId?: string;
  presetCounts: Partial<Record<VolumeViewPresetId, number>>;
  presetCountsLoading: boolean;
}

export default function ReportsFilterSection({
  activeDateFilterTitle,
  onDateFilterChange,
  setPageOffset,
  utilizationChartRef,
  usageChartRef,
  tableQueryUrl,
  tableCountUrl,
  chartQueryUrl,
  totalCount,
  isOrgView = false,
  accounts = [],
  activeOrgId,
  presetCounts,
  presetCountsLoading,
}: ReportsFilterSectionProps) {
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const { isExporting } = useExportState();
  const reportsExporting = isExporting('reports');
  const [searchParams] = useSearchParams();
  const apiFilters = searchParams.get(apiFilterKey);

  const currentBaseFilters = useMemo(
    () => safeJsonParseArray<SelectOption>(apiFilters),
    [apiFilters]
  );

  const {
    appliedClusterNameFilters,
    appliedInstanceFilters,
    appliedVolumeIdFilters,
    appliedVolumeTagFilters,
    appliedAccountFilters,
  } = useMemo(() => {
    const processedFilters = searchParams.get(PROCESSED_FILTERS_PARAM_KEY);
    const appliedClusterNameFilters: SelectOption[] = [];
    const appliedInstanceFilters: SelectOption[] = [];
    const appliedVolumeIdFilters: SelectOption[] = [];
    const appliedVolumeTagFilters: SelectOption[] = [];
    const appliedAccountFilters: SelectOption[] = [];

    const TAG_FIELD_RE = /^tag:(.+)$/;
    const VALUE_SPLIT_RE = /\s*,\s*/;

    if (processedFilters) {
      const parsedProcessedFilters = safeJsonParseArray<unknown>(processedFilters);

      parsedProcessedFilters.forEach((filterEntry) => {
        const filter =
          typeof filterEntry === 'string'
            ? safeJsonParse<Record<string, unknown> | null>(filterEntry, null)
            : (filterEntry as Record<string, unknown> | null);
        if (!filter) {
          return;
        }

        const { field, value, op } = filter;

        if (
          typeof field === 'string' &&
          (op === 'in' || op === 'equals') &&
          typeof value === 'string'
        ) {
          const values = value.split(VALUE_SPLIT_RE);
          const tagMatch = TAG_FIELD_RE.exec(field);

          values.forEach((val: string) => {
            const trimmed = val.trim();
            if (!trimmed) {
              return;
            }

            if (field === 'clusterName') {
              appliedClusterNameFilters.push({ id: trimmed, name: trimmed, group: 'clusterName' });
            } else if (field === 'instanceId') {
              appliedInstanceFilters.push({ id: trimmed, name: trimmed, group: 'instanceId' });
            } else if (field === 'volumeId') {
              appliedVolumeIdFilters.push({ id: trimmed, name: trimmed, group: 'volumeId' });
            } else if (field === 'accountId') {
              appliedAccountFilters.push({ id: trimmed, name: trimmed, group: 'accountId' });
            } else if (tagMatch) {
              const key = tagMatch[1];
              const name = key && trimmed ? `${key}:${trimmed}` : key || trimmed;
              appliedVolumeTagFilters.push({ id: name, name, group: `tag:${key}` as const });
            }
          });
        }
      });
    } else if (apiFilters) {
      // Fallback to api_filters
      appliedClusterNameFilters.push(
        ...currentBaseFilters.filter((f) => f.group === 'clusterName')
      );
      appliedInstanceFilters.push(...currentBaseFilters.filter((f) => f.group === 'instanceId'));
      appliedVolumeIdFilters.push(...currentBaseFilters.filter((f) => f.group === 'volumeId'));
      appliedAccountFilters.push(...currentBaseFilters.filter((f) => f.group === 'accountId'));
      appliedVolumeTagFilters.push(
        ...currentBaseFilters.filter(
          (f) => typeof f.group === 'string' && (f.group as string).startsWith('tag:')
        )
      );
    }

    return {
      appliedClusterNameFilters,
      appliedInstanceFilters,
      appliedVolumeIdFilters,
      appliedVolumeTagFilters,
      appliedAccountFilters,
    };
  }, [searchParams, currentBaseFilters, apiFilters]);

  const appliedAutoscaleState = useMemo(() => {
    const rawStateIds = (searchParams.get(AUTOSCALING_STATE_PARAM_KEY) || '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);
    const rawEligibilityIds = (searchParams.get(AUTOSCALING_ELIGIBILITY_PARAM_KEY) || '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);

    return migrateLegacyAutoscalingUrlParams(
      rawStateIds,
      rawEligibilityIds,
      currentBaseFilters
    ).stateIds;
  }, [searchParams, currentBaseFilters]);

  const appliedEligibility = useMemo(() => {
    const rawStateIds = (searchParams.get(AUTOSCALING_STATE_PARAM_KEY) || '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);
    const rawEligibilityIds = (searchParams.get(AUTOSCALING_ELIGIBILITY_PARAM_KEY) || '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);

    return migrateLegacyAutoscalingUrlParams(
      rawStateIds,
      rawEligibilityIds,
      currentBaseFilters
    ).eligibilityIds;
  }, [searchParams, currentBaseFilters]);

  const needsAutoscalingUrlMigration = useMemo(() => {
    const rawStateIds = (searchParams.get(AUTOSCALING_STATE_PARAM_KEY) || '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);
    const rawEligibilityIds = (searchParams.get(AUTOSCALING_ELIGIBILITY_PARAM_KEY) || '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);

    return migrateLegacyAutoscalingUrlParams(
      rawStateIds,
      rawEligibilityIds,
      currentBaseFilters
    ).didMigrate;
  }, [searchParams, currentBaseFilters]);

  const { applyAutoscalingFiltersOnly, resetFilters } = useFilterUrlParams();

  useEffect(() => {
    if (!needsAutoscalingUrlMigration) {
      return;
    }

    applyAutoscalingFiltersOnly(appliedAutoscaleState, appliedEligibility);
  }, [
    needsAutoscalingUrlMigration,
    appliedAutoscaleState,
    appliedEligibility,
    applyAutoscalingFiltersOnly,
  ]);

  const activeFilterCount =
    stripAutoscalingFiltersFromApiFilters(currentBaseFilters).length +
    countAutoscalingFilterSelections(appliedAutoscaleState, appliedEligibility);

  const activeDateFilterSelectedItem: {
    title: string;
    duration?: { start: Duration; end?: Duration };
  } = (() => {
    if (activeDateFilterTitle && activeDateFilterTitle.includes(CUSTOM_DATES_TITLE)) {
      return { title: activeDateFilterTitle };
    }
    const foundItem = dateFilterItems.find((item) => item.title === activeDateFilterTitle);
    return (
      foundItem ||
      dateFilterItems.find((item) => item.title === 'Last 3 hours') ||
      dateFilterItems[0] || { title: 'Error: No Date Filter' }
    );
  })();

  const handleResetFilters = useCallback(() => {
    setPageOffset('0');
    resetFilters();
    setSidePanelOpen(false);
  }, [resetFilters, setPageOffset]);

  const handleSelectVolumeViewPreset = useCallback(
    (presetId: VolumeViewPresetId) => {
      const preset = getVolumeViewPreset(presetId);
      setPageOffset('0');
      applyAutoscalingFiltersOnly(preset.stateIds, preset.eligibilityIds);
    },
    [applyAutoscalingFiltersOnly, setPageOffset]
  );

  return (
    <div className="px-[30px] py-[20px] flex justify-between gap-4 ">
      <VolumeViewDropdown
        appliedAutoscaleState={appliedAutoscaleState}
        appliedEligibility={appliedEligibility}
        onSelectPreset={handleSelectVolumeViewPreset}
        presetCounts={presetCounts}
        isLoading={presetCountsLoading}
      />
      <div className="ml-auto flex gap-2 z-30 items-center">
        <ChartFilterDropdown
          align="top-right"
          items={dateFilterItems}
          onSelect={onDateFilterChange}
          selectedItem={activeDateFilterSelectedItem}
        />
        <ReportsFiltersSidePanel
          open={sidePanelOpen}
          onOpenChange={setSidePanelOpen}
          onResetFilters={handleResetFilters}
          appliedClusterNameFilters={appliedClusterNameFilters}
          appliedInstanceFilters={appliedInstanceFilters}
          appliedVolumeIdFilters={appliedVolumeIdFilters}
          appliedVolumeTagFilters={appliedVolumeTagFilters}
          appliedAccountFilters={appliedAccountFilters}
          appliedAutoscaleState={appliedAutoscaleState}
          appliedEligibility={appliedEligibility}
          activeFilterCount={activeFilterCount}
          setSidePanelOpen={setSidePanelOpen}
          isSidePanelOpen={sidePanelOpen}
          isOrgView={isOrgView}
          accounts={accounts}
          activeOrgId={activeOrgId}
        />
        <Button
          variant="third"
          onClick={() => setIsExportModalOpen(true)}
          className="w-10"
          size="md-responsive"
          loading={reportsExporting}
          disabled={reportsExporting}
          aria-label="Export reports data"
          icon={<ExportIcon className="size-5" />}
        />
      </div>

      <ReportsExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        activeFilters={currentBaseFilters}
        utilizationChartRef={utilizationChartRef}
        usageChartRef={usageChartRef}
        tableQueryUrl={tableQueryUrl}
        tableCountUrl={tableCountUrl}
        chartQueryUrl={chartQueryUrl}
        activeDateFilterTitle={activeDateFilterTitle}
      />
    </div>
  );
}
